import { parse, isBefore } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { isDaylightSavingTime, determineYear, isEventUpcoming } from './utils';

// Helper to format CMS event dates to ISO format for schema
const formatCMSEventDate = (dateString) => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid event date:', dateString);
      return null;
    }

    // Determine timezone offset (Eastern Time)
    const isDST = isDaylightSavingTime(date);
    const offset = isDST ? '-04:00' : '-05:00';

    // Format as ISO 8601 in Eastern Time (not server local time)
    const formatted = formatInTimeZone(date, 'America/New_York', "yyyy-MM-dd'T'HH:mm:ss");
    return `${formatted}${offset}`;
  } catch (error) {
    console.error('Error formatting event date:', error);
    return null;
  }
};

// Schemas land inside a <script type="application/ld+json"> block injected with
// dangerouslySetInnerHTML, so "<" is escaped — a literal "</script>" in CMS text
// would otherwise terminate the block.
const toJsonLd = (schema) => JSON.stringify(schema).replace(/</g, '\\u003c');

const compactArray = (arr) => arr.filter(Boolean);

const hasValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim() !== '' && value !== '0';
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0;

  return value !== undefined && value !== null && value !== '' && value !== 0 && value !== '0';
};

const MAX_LISTING_SCHEMA_IMAGES = 10;
const MAX_LISTING_SCHEMA_FEATURES = 30;
const MAX_LISTING_SCHEMA_ROOMS = 20;
const MAX_LISTING_SCHEMA_OPEN_HOUSES = 10;
const MAX_SEARCH_RESULTS_SCHEMA_LISTINGS = 12;

const AMENITY_FEATURE_NAMES = new Set([
  'Appliances',
  'Basement',
  'Basement Type',
  'Cooling',
  'Fireplace',
  'Flex Room',
  'Garage',
  'Garage Spaces',
  'Gas',
  'Great Room',
  'Heating',
  'HOA Features',
  'Inside Features',
  'Kitchen',
  'Mechanical Systems',
  'Misc',
  'Parking',
  'Primary Bedroom',
  'Sewer',
  'Water'
]);

const limitArray = (arr, maxItems) => {
  return Array.isArray(arr) ? arr.slice(0, maxItems) : [];
};

const compactValue = (value) => {
  if (Array.isArray(value)) {
    const compactedArray = value.map(compactValue).filter((item) => {
      if (item === undefined || item === null || item === '') return false;
      if (Array.isArray(item)) return item.length > 0;
      if (typeof item === 'object') return Object.keys(item).length > 0;
      return true;
    });

    return compactedArray.length > 0 ? compactedArray : undefined;
  }

  if (value && typeof value === 'object') {
    const compactedObject = Object.entries(value).reduce((acc, [key, entryValue]) => {
      const compactedValue = compactValue(entryValue);

      if (compactedValue !== undefined) {
        acc[key] = compactedValue;
      }

      return acc;
    }, {});

    return Object.keys(compactedObject).length > 0 ? compactedObject : undefined;
  }

  if (typeof value === 'number' && !Number.isFinite(value)) return undefined;
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue === '' ? undefined : trimmedValue;
  }
  if (value === undefined || value === null || value === '') return undefined;

  return value;
};

const compactObject = (obj) => {
  return compactValue(obj) || {};
};

const SIBCY_SOCIAL_PROFILES = [
  'https://www.facebook.com/SibcyClineRealtors',
  'https://www.twitter.com/sibcycline/',
  'https://www.instagram.com/sibcycline/',
  'https://www.linkedin.com/company/sibcy-cline'
];

const SIBCY_LOGO = {
  '@type': 'ImageObject',
  url: 'https://www.sibcycline.com/images/logos/icons-logos-sibcy-horizontal.svg'
};

const SITE_URL = 'https://www.sibcycline.com';

const SITE_ORGANIZATION_ID = `${SITE_URL}/#organization`;

// Map the two-letter state codes used in community slugs to full state names.
const STATE_NAMES = {
  OH: 'Ohio',
  KY: 'Kentucky',
  IN: 'Indiana'
};

// Sibcy Cline organization node; pass an id to make it a referenceable @graph entry.
const buildSibcyOrganization = (id) =>
  compactObject({
    '@type': 'Organization',
    '@id': id,
    name: 'Sibcy Cline REALTORS®',
    url: SITE_URL,
    logo: SIBCY_LOGO
  });

const buildListingName = ({ address, city, state, zip }) => {
  const locality = [city, state].filter(Boolean).join(', ');
  const region = [locality, zip].filter(Boolean).join(' ');

  return [address, region].filter(Boolean).join(', ');
};

const buildQuantitativeValue = (value, unitText) => {
  if (!hasValue(value)) return undefined;

  return {
    '@type': 'QuantitativeValue',
    value,
    unitText
  };
};

const buildGeoCoordinates = (latitude, longitude) => {
  const lat = Number(latitude);
  const lon = Number(longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) {
    return undefined;
  }

  return {
    '@type': 'GeoCoordinates',
    latitude: lat,
    longitude: lon
  };
};

// Resolve a CMS-provided URL (which may be relative) to an absolute site URL.
const toAbsoluteUrl = (url) => {
  if (typeof url !== 'string') return undefined;

  const trimmed = url.trim();
  if (trimmed === '') return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Contentful asset urls arrive protocol-relative ("//images.ctfassets.net/...").
  // Prefixing the site origin would point them at a path that does not exist.
  if (trimmed.startsWith('//')) return `https:${trimmed}`;

  return `${SITE_URL}/${trimmed.replace(/^\/+/, '')}`;
};

// Origin of an absolute url, for building sibling links that must share it.
// Falls back to production when no usable url was supplied.
const originOf = (value) => {
  const match = typeof value === 'string' ? value.match(/^https?:\/\/[^/]+/i) : null;

  return match ? match[0] : SITE_URL;
};

const buildPropertyValue = (name, value, unitText) => {
  if (!hasValue(value)) return null;

  return compactObject({
    '@type': 'PropertyValue',
    name,
    value,
    unitText
  });
};

const toISODate = (value) => {
  if (!value) return undefined;

  const date = new Date(value);

  if (isNaN(date.getTime())) return undefined;

  return date.toISOString();
};

const getPhotoUrl = (photo) =>
  photo?.extraLargeImageUrl ||
  photo?.midSizeImageUrl ||
  (typeof photo === 'string' ? photo : undefined);

const buildImageObject = (photo, listingName, index) => {
  const url = getPhotoUrl(photo);
  if (!url) return null;

  return compactObject({
    '@type': 'ImageObject',
    '@id': `${url}#image`,
    url,
    contentUrl: url,
    name: photo?.title || `${listingName} photo ${index + 1}`,
    width: photo?.extraLargeImageWidth,
    height: photo?.extraLargeImageHeight
  });
};

const buildImageObjects = (photos, fallbackPhoto, listingName) => {
  const selectedPhotos =
    photos?.length > 0 ? limitArray(photos, MAX_LISTING_SCHEMA_IMAGES) : [fallbackPhoto];

  return selectedPhotos
    .map((photo, index) => buildImageObject(photo, listingName, index))
    .filter(Boolean);
};

const buildPersonSchema = (agent) => {
  if (!agent?.name && !agent?.displayEmail && !agent?.displayPhone) return null;

  const agentProfileUrl = agent.agentId ? `${SITE_URL}/agent/${agent.agentId}` : undefined;

  return compactObject({
    '@type': ['Person', 'RealEstateAgent'],
    '@id': agentProfileUrl ? `${agentProfileUrl}#agent` : undefined,
    identifier: agent.agentId,
    name: agent.name || [agent.firstName, agent.lastName].filter(Boolean).join(' '),
    alternateName: agent.nickname,
    jobTitle: agent.professionalTitle,
    email: agent.displayEmail,
    telephone: agent.displayPhone,
    url: agentProfileUrl,
    sameAs: agent.displayWebsiteUrl,
    image: agent.individualPhotoUrl || agent.displayPhotoUrl,
    knowsLanguage: buildLanguageValues(agent.languages),
    knowsAbout: uniqueList(splitList(agent.designations))
  });
};

const buildAmenityFeature = (feature) => {
  if (!feature?.name || !AMENITY_FEATURE_NAMES.has(feature.name) || !hasValue(feature.value)) {
    return null;
  }

  return compactObject({
    '@type': 'LocationFeatureSpecification',
    name: feature.name,
    value: feature.value
  });
};

const getFeatureValue = (features, names) => {
  if (!Array.isArray(features)) return undefined;

  const normalizedNames = names.map((name) => name.toLowerCase());
  const matchingFeature = features.find((feature) => {
    return normalizedNames.includes(feature?.name?.toLowerCase());
  });

  return matchingFeature?.value;
};

const getNumericFeatureValue = (features, names) => {
  const value = getFeatureValue(features, names);
  const parsedValue = Number.parseInt(value, 10);

  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const buildListingOfficeSchema = (listingCourtesyOf, organizationId) => {
  if (!listingCourtesyOf) return null;

  const officeMatch = listingCourtesyOf.match(/,\s*([^,]+Office)\s+(.+)$/i);

  if (!officeMatch) return null;

  const [, officeName, officePhone] = officeMatch;
  const officeId = `https://www.sibcycline.com/#${officeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

  return compactObject({
    '@type': 'RealEstateAgent',
    '@id': officeId,
    name: officeName.trim(),
    telephone: officePhone.trim(),
    parentOrganization: {
      '@id': organizationId
    }
  });
};

const getResidenceSchemaTypes = (propertyTypeDisplay) => {
  const propertyType = propertyTypeDisplay?.toLowerCase() || '';

  if (propertyType.includes('single')) return ['Residence', 'SingleFamilyResidence'];
  if (propertyType.includes('condo') || propertyType.includes('apartment')) {
    return ['Residence', 'Apartment'];
  }
  if (propertyType.includes('house') || propertyType.includes('home'))
    return ['Residence', 'House'];

  // Accommodation carries the numberOfBedrooms/Bathrooms properties, which are
  // not valid on a bare Residence (they are siblings under Place in schema.org).
  return ['Residence', 'Accommodation'];
};

const getOfferAvailability = (status) => {
  if (!status) return undefined;

  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus.includes('sold') || normalizedStatus.includes('closed')) {
    return 'https://schema.org/SoldOut';
  }

  if (normalizedStatus.includes('pending')) {
    return 'https://schema.org/LimitedAvailability';
  }

  if (normalizedStatus.includes('active')) {
    return 'https://schema.org/InStock';
  }

  return undefined;
};

const buildListingBreadcrumbs = ({ state, zip, county, city, address, canonicalUrl }) => {
  const makeResultsUrl = (params) => {
    const query = new URLSearchParams(params);
    return `https://www.sibcycline.com/results?${query.toString()}`;
  };

  return [
    state && {
      name: state,
      item: `https://www.sibcycline.com/browse?${new URLSearchParams({
        search_type: 'all_listings',
        state
      }).toString()}`
    },
    zip &&
      state && {
        name: zip,
        item: makeResultsUrl({
          zip: `${zip}_${state}`,
          states: state,
          status: 'Active,Pending'
        })
      },
    county &&
      state && {
        name: `${county} County`,
        item: makeResultsUrl({
          counties: `${county}_${state}`,
          states: state,
          status: 'Active,Pending'
        })
      },
    city &&
      state && {
        name: city,
        item: makeResultsUrl({
          places: `${city}_${state}`,
          states: state,
          status: 'Active,Pending'
        })
      },
    address &&
      canonicalUrl && {
        name: address,
        item: canonicalUrl
      }
  ].filter((crumb) => crumb?.name && crumb?.item);
};

export const formatEventDatesToISO = (input) => {
  const parts = input.split(', '); // Splits "SUN, APR 21, 12PM - 1:30PM" into parts

  if (parts.length < 3) {
    console.error('Incorrect format. Expected input: "Day, MMM DD, startTime - endTime"');
    return {};
  }

  const datePart = parts[1].trim(); // "APR 21"
  const timeRange = parts[2].trim(); // "12PM - 1:30PM"
  const [startTime, endTime] = timeRange.split(' - ').map((time) => time.trim());

  // Handles both hours only and hours:minutes formats
  const parseFormat = (time) => {
    return time.includes(':') ? 'MMM dd yyyy h:mma' : 'MMM dd yyyy hha';
  };

  const year = determineYear(datePart);
  const fullStartDateTimeStr = `${datePart} ${year} ${startTime}`;
  const fullEndDateTimeStr = `${datePart} ${year} ${endTime}`;

  try {
    // parse() creates a Date in local time; reinterpret those local values as Eastern Time
    const startDateTime = fromZonedTime(
      parse(fullStartDateTimeStr, parseFormat(startTime), new Date()),
      'America/New_York'
    );
    const endDateTime = fromZonedTime(
      parse(fullEndDateTimeStr, parseFormat(endTime), new Date()),
      'America/New_York'
    );

    if (isNaN(startDateTime) || isNaN(endDateTime)) {
      console.error('Failed to parse one or both of the times.');
      return {};
    }

    // Skip ended events silently; in-progress events are still valid
    if (isBefore(endDateTime, new Date())) {
      return {};
    }

    const isStartDateDST = isDaylightSavingTime(startDateTime);
    const startDateOffset = isStartDateDST ? '-04:00' : '-05:00';

    const isEndDateDST = isDaylightSavingTime(endDateTime);
    const endDateOffset = isEndDateDST ? '-04:00' : '-05:00';

    const startDateISO = formatInTimeZone(
      startDateTime,
      'America/New_York',
      "yyyy-MM-dd'T'HH:mm:ss"
    );
    const endDateISO = formatInTimeZone(endDateTime, 'America/New_York', "yyyy-MM-dd'T'HH:mm:ss");

    return {
      startDate: `${startDateISO}${startDateOffset}`,
      endDate: `${endDateISO}${endDateOffset}`
    };
  } catch (error) {
    console.error('Date processing error:', error.message);
    throw error; // Re-throw the error after logging
  }
};

const splitList = (value) => {
  if (Array.isArray(value)) return value.map((item) => `${item}`.trim()).filter(Boolean);
  if (!value || typeof value !== 'string') return [];

  return value
    .split(/[,;|]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const uniqueList = (items) => Array.from(new Set(items.filter(Boolean)));

const buildLanguageValues = (languages) => {
  return splitList(languages).map((language) => ({
    '@type': 'Language',
    name: language
  }));
};

const REAL_ESTATE_LICENSING_BODIES = {
  Ohio: 'Ohio Division of Real Estate & Professional Licensing',
  Kentucky: 'Kentucky Real Estate Commission',
  Indiana: 'Indiana Professional Licensing Agency'
};

const buildLicenseCredential = (state) => {
  return {
    '@type': 'EducationalOccupationalCredential',
    name: `${state} Real Estate License`,
    credentialCategory: 'Real estate license',
    recognizedBy: {
      '@type': 'GovernmentOrganization',
      name: REAL_ESTATE_LICENSING_BODIES[state] || state
    }
  };
};

const buildLicenseCredentials = (agent) => {
  return compactArray([
    agent?.isOhioLicensed && buildLicenseCredential('Ohio'),
    agent?.isKentuckyLicensed && buildLicenseCredential('Kentucky'),
    agent?.isIndianaLicensed && buildLicenseCredential('Indiana')
  ]);
};

const buildAreaServed = (office) => {
  const areas =
    office?.areasServing?.length > 0
      ? office.areasServing
      : office?.statesServing?.length > 0
        ? office.statesServing
        : [[office?.city, office?.state].filter(Boolean).join(', ')];

  return uniqueList(areas).map((area) => ({
    '@type': 'AdministrativeArea',
    name: area
  }));
};

// Tag-shaped: "</b>", "<img src=x>" — but not "< $300k" or "<$300k".
const TAG_PATTERN = /<\/?[a-zA-Z][^>]*>/g;

// Tags are stripped both before and after entity decoding: the first pass removes
// real markup, the second removes markup that was entity-encoded ("&lt;b&gt;"),
// while decoded text like "Homes &lt; $300k" survives because it isn't tag-shaped.
// &amp; decodes last so "&amp;lt;" resolves to "&lt;", not "<".
const stripHtml = (html) =>
  String(html ?? '')
    .replace(TAG_PATTERN, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(TAG_PATTERN, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?;:])/g, '$1')
    .trim();

/**
 * Builds schema.org FAQPage JSON-LD from an array of question/answer pairs.
 * Mirrors the visible FAQ block so the structured data matches the on-page content.
 * @param {Array<{question: string, answer: string}>} qaPairs
 * @returns {string|null} stringified JSON-LD, or null when there is nothing valid
 */
export const generateFAQSchema = (qaPairs) => {
  if (!Array.isArray(qaPairs)) return null;

  const mainEntity = qaPairs
    .map((qa) => {
      const questionText = stripHtml(qa?.question);
      const answerText = stripHtml(qa?.answer);
      if (!questionText || !answerText) return null;
      return {
        '@type': 'Question',
        name: questionText,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText
        }
      };
    })
    .filter(Boolean);

  if (mainEntity.length === 0) return null;

  return toJsonLd({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity
  });
};

// Build an ImageObject from a CMS image that may be a bare URL string or an asset
// object ({ url, width, height }). Featured image URLs are forced absolute so they
// are always crawlable. Returns undefined when no URL resolves.
const buildArticleImage = (image) => {
  const url = toAbsoluteUrl(typeof image === 'string' ? image : image?.url);
  if (!url) return undefined;

  return compactObject({
    '@type': 'ImageObject',
    url,
    contentUrl: url,
    width: typeof image === 'object' ? image?.width : undefined,
    height: typeof image === 'object' ? image?.height : undefined
  });
};

/**
 * Builds a schema.org @graph for a blog post: a BlogPosting anchored to the Sibcy
 * Cline Organization (publisher), plus a BreadcrumbList. Every field is optional
 * except `title`; missing values are omitted rather than filled with placeholders.
 * Key takeaways, when present, are surfaced as the article `abstract`.
 *
 * @param {object} post
 * @param {string} post.title - headline (matches the visible page title)
 * @param {string} [post.author] - byline; falls back to the Sibcy Cline org when absent
 * @param {string} [post.publishDate] - editorial publish date
 * @param {string} [post.modifiedDate] - last-updated date (e.g. CMS sys.publishedAt)
 * @param {string|{url: string, width?: number, height?: number}} [post.image] - featured image
 * @param {string} [post.description] - excerpt / meta description
 * @param {string} [post.url] - absolute canonical post URL
 * @param {string[]} [post.keyTakeaways]
 * @param {Array<{title: string, slug?: string, categoryId?: string|number}>} [post.categories] -
 *   topics; sorted by categoryId so the breadcrumb's category crumb is stable
 * @returns {string|null} stringified JSON-LD, or null when there's no title
 */
export const generateBlogPostingSchema = ({
  title,
  author,
  publishDate,
  modifiedDate,
  image,
  description,
  url,
  keyTakeaways,
  categories
} = {}) => {
  if (!title) return null;

  const organizationId = SITE_ORGANIZATION_ID;
  const articleId = url ? `${url}#article` : undefined;
  const breadcrumbId = url ? `${url}#breadcrumb` : undefined;
  // Sibling links follow the page's own origin so a beta/preview deploy does not
  // mix its page url with production breadcrumb and category urls.
  const siteOrigin = originOf(url);

  // Use a Person when a real byline exists; fall back to the Sibcy Cline
  // organization when the author is missing or is the company itself. Never emit a
  // placeholder author.
  const authorName = typeof author === 'string' ? author.trim() : '';
  const isCompanyAuthor = /sibcy\s*cline/i.test(authorName);
  const authorNode =
    authorName && !isCompanyAuthor
      ? { '@type': 'Person', name: authorName }
      : { '@id': organizationId };

  // Topics/categories drive keywords, articleSection, and `about` references.
  const topics = (Array.isArray(categories) ? categories : [])
    .map((category) => ({
      title: typeof category?.title === 'string' ? category.title.trim() : '',
      slug: typeof category?.slug === 'string' ? category.slug.trim() : '',
      categoryId: category?.categoryId
    }))
    .filter((category) => category.title)
    // Contentful returns `categoryId_in` matches in its own order, so sort to keep
    // the category crumb stable across builds for a multi-category post.
    .sort((a, b) =>
      String(a.categoryId ?? '').localeCompare(String(b.categoryId ?? ''), 'en', {
        numeric: true
      })
    );
  const topicNames = uniqueList(topics.map((topic) => topic.title));
  const aboutTopics = compactArray(
    topics.map((topic) =>
      compactObject({
        '@type': 'Thing',
        name: topic.title,
        url: topic.slug ? `${siteOrigin}/blog/category/${topic.slug}` : undefined
      })
    )
  );

  const takeaways = (Array.isArray(keyTakeaways) ? keyTakeaways : [])
    .map((takeaway) => stripHtml(takeaway))
    .filter(Boolean);

  const article = compactObject({
    '@type': 'BlogPosting',
    '@id': articleId,
    headline: stripHtml(title),
    description: stripHtml(description) || undefined,
    image: buildArticleImage(image),
    datePublished: toISODate(publishDate),
    // Keep dateModified populated; fall back to the publish date when the CMS has
    // no distinct last-updated timestamp.
    dateModified: toISODate(modifiedDate) || toISODate(publishDate),
    author: authorNode,
    publisher: { '@id': organizationId },
    url,
    mainEntityOfPage: url ? { '@type': 'WebPage', '@id': url } : undefined,
    keywords: topicNames.length > 0 ? topicNames : undefined,
    articleSection: topicNames.length > 0 ? topicNames : undefined,
    about: aboutTopics.length > 0 ? aboutTopics : undefined,
    abstract: takeaways.length > 0 ? takeaways.join(' ') : undefined
  });

  const breadcrumb = url
    ? {
        '@type': 'BreadcrumbList',
        '@id': breadcrumbId,
        itemListElement: compactArray([
          { name: 'Home', item: siteOrigin },
          { name: 'Blog', item: `${siteOrigin}/blog` },
          topics[0]?.slug && {
            name: topics[0].title,
            item: `${siteOrigin}/blog/category/${topics[0].slug}`
          },
          { name: stripHtml(title), item: url }
        ]).map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: { '@id': crumb.item, name: crumb.name }
        }))
      }
    : undefined;

  const graph = compactArray([buildSibcyOrganization(organizationId), article, breadcrumb]);

  // toJsonLd escapes "<" so schema text cannot terminate the script block.
  return toJsonLd({
    '@context': 'https://schema.org',
    '@graph': graph
  });
};

export const generateRealEstateSchema = (propertyData) => {
  const {
    acres,
    address,
    bathroomCount,
    bedroomCount,
    city,
    state,
    canonicalUrl,
    county,
    dataRefreshed,
    daysSinceEntered,
    daysSinceLastPriceChange,
    daysSinceNew,
    features,
    fullBathroomCount,
    isNewConstruction,
    listingCourtesyOf,
    listingId,
    marketingRemarks,
    mlsNumber,
    zip,
    partBathroomCount,
    photos,
    price,
    mainPhoto,
    openHouses,
    primaryDisplayAgent,
    propertyTypeDisplay,
    rooms,
    schoolDistrict,
    secondaryDisplayAgent,
    squareFootage,
    status,
    streetName,
    suburb,
    videoUrl,
    onMarketDate,
    latitude,
    longitude
  } = propertyData;

  const listingName = buildListingName({ address, city, state, zip });
  const listingUrl =
    canonicalUrl || (hasValue(listingId) ? `${SITE_URL}/listing/${listingId}` : undefined);
  const listingIdBase = listingUrl ? `${listingUrl}#listing` : undefined;
  const residenceId = listingIdBase ? `${listingIdBase}-residence` : undefined;
  const placeId = listingIdBase ? `${listingIdBase}-place` : undefined;
  const offerId = listingIdBase ? `${listingIdBase}-offer` : undefined;
  const videoId = listingIdBase ? `${listingIdBase}-video` : undefined;
  const breadcrumbId = listingIdBase ? `${listingIdBase}-breadcrumbs` : undefined;
  const organizationId = SITE_ORGANIZATION_ID;
  const webpageId = canonicalUrl
    ? `${canonicalUrl}#webpage`
    : listingIdBase
      ? `${listingIdBase}-webpage`
      : undefined;
  const listingOffice = buildListingOfficeSchema(listingCourtesyOf, organizationId);
  const listingOfficeRef = listingOffice ? { '@id': listingOffice['@id'] } : null;
  const geoCoordinates = buildGeoCoordinates(latitude, longitude);
  const schemaPhotos = buildImageObjects(photos, mainPhoto, listingName);
  const primaryImage = schemaPhotos[0]?.url || mainPhoto?.midSizeImageUrl;
  const schemaImages = schemaPhotos.length > 0 ? schemaPhotos : primaryImage;
  const listingAgents = compactArray(
    [primaryDisplayAgent, secondaryDisplayAgent].map(buildPersonSchema)
  );
  const limitedFeatures = limitArray(features, MAX_LISTING_SCHEMA_FEATURES);
  const limitedRooms = limitArray(rooms, MAX_LISTING_SCHEMA_ROOMS);
  const propertyFacts = [
    buildPropertyValue('MLS Number', mlsNumber),
    buildPropertyValue('Listing ID', listingId),
    buildPropertyValue('Property Type', propertyTypeDisplay),
    buildPropertyValue('Status', status),
    buildPropertyValue('County', county),
    buildPropertyValue('School District', schoolDistrict),
    buildPropertyValue('Subdivision', suburb),
    buildPropertyValue('Street Name', streetName),
    buildPropertyValue('Lot Size (Acres)', acres),
    buildPropertyValue('New Construction', isNewConstruction),
    buildPropertyValue('Days Since New', daysSinceNew),
    buildPropertyValue('Days Since Entered', daysSinceEntered),
    buildPropertyValue('Days Since Last Price Change', daysSinceLastPriceChange),
    ...limitedFeatures.map((feature) => buildPropertyValue(feature.name, feature.value)),
    ...limitedRooms.map((room) => buildPropertyValue(room.name, room.description))
  ].filter(Boolean);
  const amenityFeatures = compactArray(limitedFeatures.map(buildAmenityFeature));
  const yearBuilt = getNumericFeatureValue(features, ['Year Built', 'YearBuilt', 'Built']);
  const breadcrumbs = buildListingBreadcrumbs({
    state,
    zip,
    county,
    city,
    address,
    canonicalUrl
  });

  let graph = [
    compactObject({
      '@type': 'WebPage',
      '@id': webpageId,
      url: listingUrl,
      name: listingName,
      description: marketingRemarks,
      inLanguage: 'en-US',
      primaryImageOfPage: primaryImage
        ? {
            '@type': 'ImageObject',
            url: primaryImage
          }
        : undefined,
      mainEntity: {
        '@id': listingIdBase
      },
      about: {
        '@id': residenceId
      },
      publisher: {
        '@id': organizationId
      },
      breadcrumb: {
        '@id': breadcrumbId
      },
      datePublished: toISODate(onMarketDate),
      dateModified: toISODate(dataRefreshed)
    }),
    buildSibcyOrganization(organizationId),
    compactObject({
      '@type': 'RealEstateListing',
      '@id': listingIdBase,
      identifier: [
        buildPropertyValue('Listing ID', listingId),
        buildPropertyValue('MLS Number', mlsNumber)
      ].filter(Boolean),
      additionalType: propertyTypeDisplay,
      description: marketingRemarks,
      image: schemaImages,
      name: listingName,
      url: listingUrl,
      datePosted: toISODate(onMarketDate),
      dateModified: toISODate(dataRefreshed),
      provider: compactArray([
        ...(listingAgents.length > 0 ? listingAgents : [{ '@id': organizationId }]),
        listingOfficeRef
      ]),
      creditText: listingCourtesyOf,
      offers: {
        '@id': offerId
      },
      contentLocation: {
        '@id': placeId
      },
      about: {
        '@id': residenceId
      },
      mainEntityOfPage: {
        '@id': webpageId
      },
      video: videoUrl ? { '@id': videoId } : undefined
    }),
    compactObject({
      '@type': 'Offer',
      '@id': offerId,
      price,
      priceCurrency: 'USD',
      availability: getOfferAvailability(status),
      url: listingUrl,
      businessFunction: 'https://purl.org/goodrelations/v1#Sell',
      itemOffered: {
        '@id': residenceId
      },
      seller: listingOfficeRef || { '@id': organizationId }
    }),
    compactObject({
      '@type': 'Place',
      '@id': placeId,
      name: listingName,
      address: {
        '@type': 'PostalAddress',
        streetAddress: address,
        addressLocality: city,
        addressRegion: state,
        postalCode: zip,
        addressCountry: 'US'
      },
      geo: geoCoordinates,
      containedInPlace: county
        ? {
            '@type': 'AdministrativeArea',
            name: `${county} County`
          }
        : undefined
    }),
    compactObject({
      '@type': getResidenceSchemaTypes(propertyTypeDisplay),
      '@id': residenceId,
      additionalType: propertyTypeDisplay,
      name: listingName,
      description: marketingRemarks,
      address: {
        '@type': 'PostalAddress',
        streetAddress: address,
        addressLocality: city,
        addressRegion: state,
        postalCode: zip,
        addressCountry: 'US'
      },
      geo: geoCoordinates,
      image: schemaImages,
      floorSize: buildQuantitativeValue(squareFootage, 'SQFT'),
      numberOfBedrooms: bedroomCount,
      numberOfBathroomsTotal: bathroomCount,
      numberOfFullBathrooms: fullBathroomCount,
      numberOfPartialBathrooms: partBathroomCount,
      additionalProperty: propertyFacts,
      amenityFeature: amenityFeatures,
      yearBuilt
    }),
    compactObject({
      '@type': 'BreadcrumbList',
      '@id': breadcrumbId,
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': crumb.item,
          name: crumb.name
        }
      }))
    })
  ];

  if (listingOffice) {
    graph.push(listingOffice);
  }

  // Only emit a VideoObject for an actual video. A virtual-tour URL (e.g. Matterport/3D)
  // isn't guaranteed to be a video and would fail Google's video validation.
  if (videoUrl) {
    graph.push(
      compactObject({
        '@type': 'VideoObject',
        '@id': videoId,
        description: `Watch the video tour of ${listingName}!`,
        name: `Video Tour of ${listingName}`,
        thumbnailUrl: primaryImage,
        contentUrl: videoUrl,
        embedUrl: videoUrl,
        url: videoUrl,
        // Logic below is to handle listings not yet on the market with videos
        uploadDate:
          onMarketDate && isBefore(new Date(onMarketDate), new Date())
            ? new Date(onMarketDate).toISOString()
            : new Date().toISOString()
      })
    );
  }

  if (Array.isArray(openHouses) && openHouses.length > 0) {
    const openHouseEvents = limitArray(openHouses, MAX_LISTING_SCHEMA_OPEN_HOUSES)
      .map((openHouse) => {
        const openHouseTimes = formatEventDatesToISO(openHouse.formattedDateTimeDisplay);

        if (!openHouseTimes.startDate || !openHouseTimes.endDate) {
          return null;
        }

        return compactObject({
          '@type': 'Event',
          '@id': listingIdBase
            ? `${listingIdBase}-open-house-${openHouseTimes.startDate}`
            : undefined,
          endDate: openHouseTimes.endDate,
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@id': placeId
          },
          name: `Open House at ${listingName}`,
          description: `Open House of ${address}`,
          image: schemaImages,
          organizer: {
            '@id': organizationId
          },
          eventStatus: 'https://schema.org/EventScheduled',
          startDate: openHouseTimes.startDate,
          url: listingUrl
        });
      })
      .filter((event) => event !== null);

    graph = graph.concat(openHouseEvents);
  }

  return toJsonLd({
    '@context': 'https://schema.org',
    '@graph': compactArray(graph)
  });
};

export const generateWebsiteSchema = (meta) => {
  return toJsonLd({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Sibcy Cline REALTORS®',
    url: `${meta.url}`,
    publisher: {
      '@type': 'Organization',
      name: 'Sibcy Cline REALTORS®',
      logo: {
        '@type': 'ImageObject',
        url: `${meta.imageUrl}`
      }
    },
    inLanguage: ['en'],
    accessibilityFeature: 'ARIA landmarks',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate:
          'https://www.sibcycline.com/results?bathrooms={bathrooms}&bedrooms={bedrooms}&garageSpaces={garageSpaces}&maxAcres={maxAcres}&maxPrice={maxPrice}&minAcres={minAcres}&minPrice={minPrice}&minSquareFootage={minSquareFootage}&propertySubSelectIds={propertySubSelectIds}&propertyTypeId={propertyTypeId}&sortby={sort_order}&status={status}&page={page}&hasBasementTypePartiallyFinished={hasBasementTypePartiallyFinished}&hasBasementTypeUnfinished={hasBasementTypeUnfinished}&hasBasementTypeFinished={hasBasementTypeFinished}&hasHeatTypeElectric={hasHeatTypeElectric}&hasHeatTypeForcedAir={hasHeatTypeForcedAir}&hasHeatTypeGravity={hasHeatTypeGravity}&hasHeatTypeGeothermal={hasHeatTypeGeothermal}&hasHeatTypeHeatPump={hasHeatTypeHeatPump}&hasHeatTypeSteam={hasHeatTypeSteam}&hasHeatTypeGas={hasHeatTypeGas}&hasLevel1Bedroom={hasLevel1Bedroom}&hasLevel1Bath={hasLevel1Bath}&hasSwimmingPool={hasSwimmingPool}&hasCentralAir={hasCentralAir}&hasFamilyRoom={hasFamilyRoom}&hasFormalDiningRoom={hasFormalDiningRoom}&showOnlyNewConstructions={showOnlyNewConstructions}&showOnlyOpenHouses={showOnlyOpenHouses}&daysSinceNew={daysSinceNew}&daysSinceLastPriceChange={daysSinceLastPriceChange}&counties={counties}&places={places}&schoolDistricts={schoolDistricts}&zip={zip}'
      },
      'query-input': [
        { '@type': 'PropertyValueSpecification', valueName: 'bathrooms', valueRequired: false },
        { '@type': 'PropertyValueSpecification', valueName: 'bedrooms', valueRequired: false },
        { '@type': 'PropertyValueSpecification', valueName: 'garageSpaces', valueRequired: false },
        { '@type': 'PropertyValueSpecification', valueName: 'maxAcres', valueRequired: false },
        { '@type': 'PropertyValueSpecification', valueName: 'maxPrice', valueRequired: false },
        { '@type': 'PropertyValueSpecification', valueName: 'minAcres', valueRequired: false },
        { '@type': 'PropertyValueSpecification', valueName: 'minPrice', valueRequired: false },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'minSquareFootage',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'propertySubSelectIds',
          valueRequired: false,
          multipleValues: true
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'propertyTypeId',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'sort_order',
          valueRequired: false,
          defaultValue: 'Price Change'
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'status',
          valueRequired: false,
          multipleValues: true
        },
        { '@type': 'PropertyValueSpecification', valueName: 'page', valueRequired: false },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasBasementTypePartiallyFinished',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasBasementTypeUnfinished',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasBasementTypeFinished',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasHeatTypeElectric',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasHeatTypeForcedAir',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasHeatTypeGravity',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasHeatTypeGeothermal',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasHeatTypeHeatPump',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasHeatTypeSteam',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasHeatTypeGas',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasLevel1Bedroom',
          valueRequired: false
        },
        { '@type': 'PropertyValueSpecification', valueName: 'hasLevel1Bath', valueRequired: false },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasSwimmingPool',
          valueRequired: false
        },
        { '@type': 'PropertyValueSpecification', valueName: 'hasCentralAir', valueRequired: false },
        { '@type': 'PropertyValueSpecification', valueName: 'hasFamilyRoom', valueRequired: false },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'hasFormalDiningRoom',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'showOnlyNewConstructions',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'showOnlyOpenHouses',
          valueRequired: false
        },
        { '@type': 'PropertyValueSpecification', valueName: 'daysSinceNew', valueRequired: false },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'daysSinceLastPriceChange',
          valueRequired: false
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'counties',
          valueRequired: false,
          multipleValues: true
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'places',
          valueRequired: false,
          multipleValues: true
        },
        {
          '@type': 'PropertyValueSpecification',
          valueName: 'schoolDistricts',
          valueRequired: false,
          multipleValues: true
        },
        { '@type': 'PropertyValueSpecification', valueName: 'zip', valueRequired: false }
      ]
    },
    sameAs: SIBCY_SOCIAL_PROFILES
  });
};

export const generateAboutSchema = (meta) => {
  return toJsonLd({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    url: meta.url
  });
};

export const generateContactSchema = (meta) => {
  return toJsonLd({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    url: meta.url
  });
};

export const generateOfficeSchema = (officeData) => {
  // Handle both single office and array of offices
  const offices = Array.isArray(officeData) ? officeData : [officeData];

  const officeSchemas = offices.map((office) => {
    const {
      officeId,
      name,
      phone,
      fax,
      address,
      city,
      state,
      zipCode,
      photoUrl,
      agents = [],
      description,
      url,
      latitude,
      longitude
    } = office;

    // Build the main organization schema
    const organizationSchema = {
      '@type': 'LocalBusiness',
      '@id': `https://www.sibcycline.com/office/${officeId}`,
      name: name,
      description: description || `${name} office of Sibcy Cline REALTORS®`,
      url:
        url ||
        `https://www.sibcycline.com/office/${encodeURIComponent(name.toLowerCase().replace(/\s+/g, '-'))}`,
      telephone: phone,
      faxNumber: fax,
      image: photoUrl,
      address: {
        '@type': 'PostalAddress',
        streetAddress: address,
        addressLocality: city,
        addressRegion: state,
        postalCode: zipCode,
        addressCountry: 'US'
      },
      parentOrganization: buildSibcyOrganization(),
      sameAs: SIBCY_SOCIAL_PROFILES
    };

    // Add geo coordinates only when present and not (0, 0)
    const geo = buildGeoCoordinates(latitude, longitude);
    if (geo) {
      organizationSchema.geo = geo;
    }

    const isIndividualOfficePage = !Array.isArray(officeData);

    if (isIndividualOfficePage && agents.length > 0) {
      // For individual office pages, include all agents as employees
      organizationSchema.employee = agents.map((agentOffice) => {
        const agent = agentOffice.agent;
        return {
          '@type': 'Person',
          '@id': `${SITE_URL}/agent/${agent.agentId}`,
          name: agent.name,
          jobTitle: agent.professionalTitle || 'Real Estate Agent',
          image: agent.displayPhotoUrl,
          telephone: agent.displayPhone,
          email: agent.displayEmail
        };
      });
    }
    return organizationSchema;
  });

  const allSchemas = officeSchemas;

  return toJsonLd({
    '@context': 'https://schema.org',
    '@graph': allSchemas
  });
};

export const generateCommunityEventSchema = (eventData) => {
  const now = new Date();

  // Deduplicate events by sys.id
  const uniqueEvents = new Map();
  eventData.forEach((event) => {
    const key = event.sys?.id || event.headline;
    if (!uniqueEvents.has(key)) {
      uniqueEvents.set(key, event);
    }
  });

  // Filter to only upcoming events
  const upcomingEvents = Array.from(uniqueEvents.values())
    .filter((event) => {
      // Validate required fields
      if (!event.headline || !event.eventDate) {
        console.warn('Event missing required fields:', event);
        return false;
      }

      // Recency mirrors the shared isEventUpcoming, except only an Ongoing event
      // with an expirationDate emits an endDate below — any other event must
      // stand on a startDate that is still current (an Event node with a stale
      // startDate and no endDate reads as a past event), so its expirationDate
      // is ignored here even though the visible tab honors it.
      if (event.schedule === 'Ongoing' && event.expirationDate) {
        return isEventUpcoming(event, now);
      }
      return isEventUpcoming({ eventDate: event.eventDate }, now);
    })
    // Sort by date (earliest first)
    // For ongoing events, sort by end date; for single-day events, sort by start date
    .sort((a, b) => {
      const dateA =
        a.schedule === 'Ongoing' && a.expirationDate
          ? new Date(a.expirationDate)
          : new Date(a.eventDate);
      const dateB =
        b.schedule === 'Ongoing' && b.expirationDate
          ? new Date(b.expirationDate)
          : new Date(b.eventDate);
      return dateA - dateB;
    })
    // Limit to first 25 upcoming events (SEO best practice)
    .slice(0, 25);

  const eventSchemas = upcomingEvents
    .map((event) => {
      // Parse start date
      const startDate = formatCMSEventDate(event.eventDate);
      if (!startDate) return null; // Skip invalid dates

      // Build base event schema
      const eventSchema = {
        '@type': 'Event',
        name: event.headline,
        startDate: startDate,
        eventStatus: 'https://schema.org/EventScheduled'
      };

      // Add endDate for ongoing events only
      if (event.schedule === 'Ongoing' && event.expirationDate) {
        const endDate = formatCMSEventDate(event.expirationDate);
        if (endDate) {
          eventSchema.endDate = endDate;
        }
      }

      // Add optional fields
      if (event.eventDetails) {
        eventSchema.description = event.eventDetails;
      }

      if (event.featuredImage?.url) {
        eventSchema.image = event.featuredImage.url;
      }

      if (event.linkUrl) {
        eventSchema.url = event.linkUrl;
      }

      // Organizer attribution
      if (event.authorName) {
        eventSchema.organizer = {
          '@type': 'Organization',
          name: event.authorName
        };
      } else {
        eventSchema.organizer = {
          '@type': 'Organization',
          name: 'Local Community Event'
        };
      }

      return eventSchema;
    })
    .filter((event) => event !== null);

  // Return null if no valid events
  if (eventSchemas.length === 0) {
    return null;
  }

  return toJsonLd({
    '@context': 'https://schema.org',
    '@graph': eventSchemas
  });
};

export const generateSearchResultsSchema = ({ name, description, url, totalCount, listings }) => {
  if (!listings || listings.length === 0) return null;

  const itemListElements = limitArray(listings, MAX_SEARCH_RESULTS_SCHEMA_LISTINGS)
    .map((listing, index) => {
      if (!listing.canonicalUrl || !listing.address) return null;
      const listingName = buildListingName(listing);
      const listingImage = getPhotoUrl(listing.mainPhoto);

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: compactObject({
          '@type': 'RealEstateListing',
          '@id': `${listing.canonicalUrl}#listing`,
          url: listing.canonicalUrl,
          name: listingName,
          image: listingImage,
          offers: hasValue(listing.price)
            ? {
                '@type': 'Offer',
                price: listing.price,
                priceCurrency: 'USD',
                availability: getOfferAvailability(listing.status),
                url: listing.canonicalUrl
              }
            : undefined,
          about: compactObject({
            '@type': ['Residence', 'Accommodation'],
            name: listingName,
            address: {
              '@type': 'PostalAddress',
              streetAddress: listing.address,
              addressLocality: listing.city,
              addressRegion: listing.state,
              postalCode: listing.zip,
              addressCountry: 'US'
            },
            numberOfBedrooms: listing.bedroomCount,
            numberOfBathroomsTotal: listing.bathroomCount,
            numberOfFullBathrooms: listing.fullBathroomCount,
            numberOfPartialBathrooms: listing.partBathroomCount,
            additionalProperty: [
              buildPropertyValue('Status', listing.status),
              buildPropertyValue('MLS Number', listing.mlsNumber),
              buildPropertyValue('Lot Size (Acres)', listing.acres)
            ].filter(Boolean)
          })
        })
      };
    })
    .filter(Boolean);

  if (itemListElements.length === 0) return null;

  const itemList = {
    '@type': 'ItemList',
    numberOfItems: totalCount ?? itemListElements.length,
    itemListElement: itemListElements
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    name
  };
  if (description) schema.description = description;
  if (url) schema.url = url;
  schema.mainEntity = itemList;

  return toJsonLd(schema);
};

export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return toJsonLd({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': crumb.url,
        name: crumb.name
      }
    }))
  });
};

// Builds a self-contained @graph that identifies the specific community a page
// describes: a Place node (name, location, region, image) anchored by a WebPage,
// plus breadcrumbs, an ItemList of nearby communities, and the agents surfaced on
// the page. This replaces the generic "Explore Nearby Communities" ItemList so
// crawlers identify the page by its actual community name rather than the module
// label.
export const generateCommunitySchema = (communityData = {}) => {
  const {
    title,
    slug,
    description,
    region,
    city,
    county,
    state,
    image,
    listingsUrl,
    nearbyCommunities = [],
    agents = []
  } = communityData;

  if (!title || !slug) return null;

  const pageUrl = `${SITE_URL}/community/${slug}`;
  const communityId = `${pageUrl}#community`;
  const webPageId = `${pageUrl}#webpage`;
  const breadcrumbId = `${pageUrl}#breadcrumb`;

  const stateName = STATE_NAMES[(state || '').toUpperCase()] || state || undefined;
  const primaryImage = toAbsoluteUrl(typeof image === 'object' ? image?.url : image);

  // Only set addressLocality when a real city resolves; falling back to the
  // community title would drop a neighborhood name (e.g. "Hyde Park") into the
  // locality slot. The Place already carries the title in its `name`, so an
  // omitted locality loses nothing and keeps the address semantically correct.
  const address = compactObject({
    '@type': 'PostalAddress',
    addressLocality: city,
    addressRegion: stateName,
    addressCountry: 'US'
  });

  // Administrative hierarchy: county nested within state.
  const stateArea = stateName
    ? compactObject({ '@type': 'AdministrativeArea', name: stateName })
    : undefined;
  const countyName = county
    ? /county$/i.test(county.trim())
      ? county.trim()
      : `${county.trim()} County`
    : undefined;
  const containedInPlace = countyName
    ? compactObject({
        '@type': 'AdministrativeArea',
        name: countyName,
        containedInPlace: stateArea
      })
    : stateArea;

  const community = compactObject({
    '@type': 'Place',
    '@id': communityId,
    name: title,
    description,
    url: pageUrl,
    image: primaryImage,
    address,
    containedInPlace,
    additionalProperty: region ? buildPropertyValue('Region', region) : undefined
  });

  // Nearby communities as related Place entities in an ItemList named for this
  // community, so the list never reads as the page's own identity.
  const nearbyElements = nearbyCommunities
    .map((nearby) => {
      const url = toAbsoluteUrl(nearby?.url);
      if (!url || !nearby?.name) return null;

      return {
        '@type': 'ListItem',
        item: compactObject({
          '@type': 'Place',
          '@id': url,
          url,
          name: nearby.name
        })
      };
    })
    .filter(Boolean)
    .map((element, index) => ({ ...element, position: index + 1 }));

  const nearbyList =
    nearbyElements.length > 0
      ? {
          '@type': 'ItemList',
          '@id': `${pageUrl}#nearby-communities`,
          name: `Communities near ${title}`,
          itemListElement: nearbyElements
        }
      : undefined;

  const agentNodes = agents.map(buildPersonSchema).filter(Boolean);
  const agentReferences = agentNodes
    .map((agent) => (agent['@id'] ? { '@id': agent['@id'] } : undefined))
    .filter(Boolean);

  const breadcrumb = {
    '@type': 'BreadcrumbList',
    '@id': breadcrumbId,
    itemListElement: [
      { name: 'Home', item: SITE_URL },
      { name: 'Communities', item: `${SITE_URL}/community` },
      { name: title, item: pageUrl }
    ].map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: { '@id': crumb.item, name: crumb.name }
    }))
  };

  const webPage = compactObject({
    '@type': 'WebPage',
    '@id': webPageId,
    url: pageUrl,
    name: title,
    description,
    primaryImageOfPage: primaryImage ? { '@type': 'ImageObject', url: primaryImage } : undefined,
    publisher: { '@id': SITE_ORGANIZATION_ID },
    about: { '@id': communityId },
    mainEntity: { '@id': communityId },
    breadcrumb: { '@id': breadcrumbId },
    significantLink: toAbsoluteUrl(listingsUrl),
    mentions: agentReferences.length > 0 ? agentReferences : undefined
  });

  const graph = compactArray([
    buildSibcyOrganization(SITE_ORGANIZATION_ID),
    webPage,
    community,
    breadcrumb,
    nearbyList,
    ...agentNodes
  ]);

  return toJsonLd({
    '@context': 'https://schema.org',
    '@graph': graph
  });
};

// A URL is only safe to emit in schema when it is absolute (http/https). Relative,
// anchor, javascript:, and modal-only CTAs are rejected so we never publish a
// broken or non-resolvable action URL. Returns the trimmed value so callers emit
// exactly what was validated — a CMS link with a stray space is otherwise invalid.
const absoluteHttpUrl = (url) => {
  if (typeof url !== 'string') return undefined;

  const trimmed = url.trim();

  return /^https?:\/\//i.test(trimmed) ? trimmed : undefined;
};

/**
 * Builds schema.org Service JSON-LD for a service/program page (mortgage,
 * relocation, home services, and the /programs/* pages). Every field is
 * optional except `name` + `provider`; anything missing is omitted so the
 * output is always valid.
 *
 * @param {Object} config
 * @param {string|string[]} [config.schemaType='Service'] - schema.org type(s), e.g. 'FinancialService' or ['Service', 'FinancialService']
 * @param {string} config.name - service name (matches the visible page title)
 * @param {string} [config.serviceType]
 * @param {string} [config.description]
 * @param {string} [config.url] - absolute canonical page URL
 * @param {string} [config.audience] - audienceType, e.g. 'Home buyers'
 * @param {{name: string, url: string}} [config.provider]
 * @param {string[]} [config.areaServed]
 * @param {{name?: string, url: string, type?: string}} [config.primaryAction] - CTA
 * @returns {string|null} stringified JSON-LD, or null when there is nothing valid
 */
export const generateServiceSchema = (config = {}) => {
  const {
    schemaType = 'Service',
    name,
    serviceType,
    description,
    url,
    audience,
    provider,
    areaServed,
    primaryAction
  } = config;

  const cleanName = typeof name === 'string' ? name.trim() : '';
  const cleanDescription = typeof description === 'string' ? description.trim() : '';

  // Require the minimum a crawler needs to identify the service and its provider.
  if (!cleanName || !provider?.name) return null;

  const providerUrl = absoluteHttpUrl(provider.url);
  const pageUrl = absoluteHttpUrl(url);

  const schema = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    name: cleanName,
    provider: {
      '@type': 'RealEstateAgent',
      name: provider.name,
      ...(providerUrl ? { url: providerUrl } : {})
    }
  };

  if (serviceType) schema.serviceType = serviceType;
  if (cleanDescription) schema.description = cleanDescription;
  if (pageUrl) schema.url = pageUrl;

  if (Array.isArray(areaServed) && areaServed.length > 0) {
    schema.areaServed = areaServed.map((area) => ({ '@type': 'Place', name: area }));
  }

  if (audience) {
    schema.audience = {
      '@type': 'Audience',
      audienceType: audience
    };
  }

  // Only emit a CTA action when its URL is absolute and resolvable.
  const actionUrl = primaryAction ? absoluteHttpUrl(primaryAction.url) : undefined;

  if (actionUrl) {
    schema.potentialAction = {
      '@type': primaryAction.type || 'Action',
      ...(primaryAction.name ? { name: primaryAction.name } : {}),
      target: actionUrl
    };
  }

  return JSON.stringify(schema);
};

export const generateItemListSchema = (itemListData) => {
  const { name, items } = itemListData;

  if (!items || items.length === 0) {
    return null;
  }

  const isValidItemType = (item) => {
    return item.__typename === 'Community' || item.__typename === 'PageBuilderdevelopment';
  };

  const getItemUrl = (item) => {
    if (item.__typename === 'Community') {
      return `https://www.sibcycline.com/community/${item.slug}`;
    }

    if (item.__typename === 'PageBuilderdevelopment') {
      const category = item.category || 'Development';
      const prefix = category === 'Development' ? 'development' : 'builder';
      return `https://www.sibcycline.com/${prefix}/${item.slug}`;
    }

    return null;
  };

  const itemListElements = items
    .map((item) => {
      if (!isValidItemType(item) || !item.slug || !item.title) {
        return null;
      }

      const itemUrl = getItemUrl(item);
      if (!itemUrl) {
        return null;
      }

      return {
        '@type': 'ListItem',
        item: {
          '@type': 'Place',
          '@id': itemUrl,
          url: itemUrl,
          name: item.title || item.builderDevelopmentName
        }
      };
    })
    .filter(Boolean)
    .map((element, i) => ({ ...element, position: i + 1 }));

  if (itemListElements.length === 0) {
    return null;
  }

  return toJsonLd({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: name,
    itemListElement: itemListElements
  });
};

export const generateAgentSchema = ({ agent, office } = {}) => {
  if (!agent?.agentId && !agent?.displayPrimary && !agent?.name) return null;

  const profileUrl = agent.agentId ? `${SITE_URL}/agent/${agent.agentId}` : undefined;
  const agentId = profileUrl ? `${profileUrl}#agent` : undefined;
  const officeUrl = toAbsoluteUrl(office?.url);
  const officeId = officeUrl
    ? `${officeUrl}#office`
    : office?.officeId
      ? `${SITE_URL}/office/${office.officeId}#office`
      : undefined;
  const organizationId = SITE_ORGANIZATION_ID;
  const webpageId = profileUrl ? `${profileUrl}#webpage` : undefined;
  const agentName = agent.displayPrimary || agent.name;
  const hasOffice = Boolean(officeId && (office?.name || office?.address));
  const officeName = office?.name;
  const knowsAbout = uniqueList(splitList(agent.designations));
  const languages = buildLanguageValues(agent.languages);
  const licenseCredentials = buildLicenseCredentials(agent);
  const areaServed = buildAreaServed(office);
  const sameAs = uniqueList([
    agent.displayWebsiteUrl,
    ...(agent.socialMediaLinks || []).map((link) => link?.linkUrl)
  ]).map(toAbsoluteUrl);
  const memberOf = compactArray([
    { '@id': organizationId },
    officeId && { '@id': officeId },
    agent.teamName && {
      '@type': 'Organization',
      name: agent.teamName
    }
  ]);

  const graph = compactArray([
    compactObject({
      '@type': 'WebPage',
      '@id': webpageId,
      url: profileUrl,
      name: agentName,
      description: agent.displayBiography,
      inLanguage: 'en-US',
      primaryImageOfPage: toAbsoluteUrl(agent.individualPhotoUrl || agent.displayPhotoUrl)
        ? {
            '@type': 'ImageObject',
            url: toAbsoluteUrl(agent.individualPhotoUrl || agent.displayPhotoUrl)
          }
        : undefined,
      mainEntity: {
        '@id': agentId
      },
      publisher: {
        '@id': organizationId
      }
    }),
    buildSibcyOrganization(organizationId),
    hasOffice &&
      compactObject({
        '@type': ['RealEstateAgent', 'LocalBusiness'],
        '@id': officeId,
        name: officeName,
        url: officeUrl,
        telephone: office.phone,
        faxNumber: office.fax,
        image: toAbsoluteUrl(office.photoUrl),
        address: {
          '@type': 'PostalAddress',
          streetAddress: office.address,
          addressLocality: office.city,
          addressRegion: office.state,
          postalCode: office.zipCode,
          addressCountry: 'US'
        },
        geo: buildGeoCoordinates(office.latitude, office.longitude),
        areaServed,
        parentOrganization: {
          '@id': organizationId
        },
        employee: {
          '@id': agentId
        }
      }),
    compactObject({
      '@type': ['Person', 'RealEstateAgent'],
      '@id': agentId,
      identifier: agent.agentId,
      name: agentName,
      givenName: agent.firstName,
      familyName: agent.lastName,
      alternateName: agent.nickname,
      jobTitle: agent.professionalTitle || agent.displaySecondary || 'Real Estate Agent',
      description: agent.displayBiography,
      url: profileUrl,
      image: toAbsoluteUrl(agent.individualPhotoUrl || agent.displayPhotoUrl),
      telephone: agent.displayPhone,
      email: agent.displayEmail,
      sameAs,
      memberOf,
      worksFor: officeId ? { '@id': officeId } : { '@id': organizationId },
      contactPoint:
        agent.displayPhone || agent.displayEmail
          ? compactObject({
              '@type': 'ContactPoint',
              contactType: 'sales',
              telephone: agent.displayPhone,
              email: agent.displayEmail,
              url: profileUrl,
              availableLanguage: languages
            })
          : undefined,
      areaServed,
      knowsAbout,
      knowsLanguage: languages,
      hasCredential: licenseCredentials
    })
  ]);

  return toJsonLd({
    '@context': 'https://schema.org',
    '@graph': graph
  });
};
